import { useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../contexts/JWTContext';
import { RBACContext } from '../contexts/user/RBACContext';
import { 
  hasPermission, 
  hasAnyPermission, 
  hasAllPermissions, 
  hasResourcePermission,
  isAdmin,
  PERMISSIONS,
  RESOURCES,
  ACTIONS 
} from '../utils/permissions';

/**
 * Custom hook for permission management
 * Provides easy access to permission checking functions and current user permissions
 */
const usePermissions = (projectId = null) => {
  const authContext = useContext(AuthContext);
  const rbacContext = useContext(RBACContext);

  // Get user role and type
  const userRole = authContext?.userType;
  const user = authContext?.user;
  
  // Get permissions from auth context or directly from user object.
  // Memoized so downstream callbacks (e.g. getPermissionsForSite) stay stable across renders.
  const authContextPermissions = authContext?.permissions;

  const extractedPermissions = useMemo(() => {
    if (!user) return [];
    const rolePermissions = user?.role?.permissions || [];
    const projectPermissions = user?.projects?.flatMap(p => p.permissions || []) || [];
    return [...new Set([...rolePermissions, ...projectPermissions])];
  }, [user]);

  const globalPermissions = useMemo(() => {
    const contextPerms = authContextPermissions || [];
    if (contextPerms.length === 0 || extractedPermissions.length > contextPerms.length) {
      return extractedPermissions;
    }
    return contextPerms;
  }, [authContextPermissions, extractedPermissions]);
  
  // Check for role in different places:
  // 1. If role is an object, use its name
  // 2. Check if user has Administrator role in any project
  // 3. Fall back to userType
  let actualUserRole = userRole;
  
  if (user?.role?.name) {
    actualUserRole = user.role.name;
  } else if (user?.role && typeof user.role === 'string') {
    actualUserRole = user.role;
  }
  
  // Check if user has Administrator role in any project
  const hasAdminProjectRole = user?.projects?.some(
    project => project.roleName === 'Administrator' || 
               project.roleName === 'Admin' ||
               project.roleName?.toLowerCase() === 'administrator'
  );
  
  // Get project-specific permissions if projectId is provided
  const projectPermissions = useMemo(
    () => rbacContext?.projectPermissions || [],
    [rbacContext?.projectPermissions]
  );
  
  // Combine global and project permissions
  const allPermissions = useMemo(() => {
    if (projectId && projectPermissions.length > 0) {
      return [...new Set([...globalPermissions, ...projectPermissions])];
    }
    return globalPermissions;
  }, [globalPermissions, projectPermissions, projectId]);

  // Removed debug logging to prevent performance issues

  // Check if user has a specific permission
  const checkPermission = useCallback((permission) => {
    // Admin users have all permissions
    if (isAdmin(allPermissions, actualUserRole) || hasAdminProjectRole) {
      return true;
    }
    // Check if user has the permission (now includes project permissions)
    return hasPermission(allPermissions, permission);
  }, [allPermissions, actualUserRole, hasAdminProjectRole]);

  // Check if user has any of the specified permissions
  const checkAnyPermission = useCallback((permissions) => {
    // Admin users have all permissions
    if (isAdmin(allPermissions, actualUserRole) || hasAdminProjectRole) {
      return true;
    }
    return hasAnyPermission(allPermissions, permissions);
  }, [allPermissions, actualUserRole, hasAdminProjectRole]);

  // Check if user has all specified permissions
  const checkAllPermissions = useCallback((permissions) => {
    // Admin users have all permissions
    if (isAdmin(allPermissions, actualUserRole) || hasAdminProjectRole) {
      return true;
    }
    return hasAllPermissions(allPermissions, permissions);
  }, [allPermissions, actualUserRole, hasAdminProjectRole]);

  // Check if user has permission for a specific resource action
  const checkResourcePermission = useCallback((resource, action) => {
    // Admin users have all permissions
    if (isAdmin(allPermissions, actualUserRole) || hasAdminProjectRole) {
      return true;
    }
    return hasResourcePermission(allPermissions, resource, action);
  }, [allPermissions, actualUserRole, hasAdminProjectRole]);

  // Check if user is admin (global admin based on role, not project-specific)
  const checkIsAdmin = useCallback(() => {
    // Only check the main user role, not project-specific admin status
    return isAdmin(allPermissions, actualUserRole);
  }, [allPermissions, actualUserRole]);

  // Check if user can access sites
  const canAccessSites = useCallback(() => {
    return checkIsAdmin() || checkAnyPermission([
      PERMISSIONS.SITES_READ,
      PERMISSIONS.SITES_WRITE,
      PERMISSIONS.SITES_DELETE
    ]);
  }, [checkIsAdmin, checkAnyPermission]);

  // Check if user can manage WAF
  const canManageWAF = useCallback(() => {
    return checkIsAdmin() || checkAnyPermission([
      PERMISSIONS.WAF_WRITE,
      PERMISSIONS.WAF_CONFIGURE
    ]);
  }, [checkIsAdmin, checkAnyPermission]);

  // Check if user can manage users (org users/roles; aligns with users:manage)
  const canManageUsers = useCallback(() => {
    return checkIsAdmin() || checkPermission(PERMISSIONS.USERS_MANAGE);
  }, [checkIsAdmin, checkPermission]);

  // Check if user can view analytics
  const canViewAnalytics = useCallback(() => {
    return checkIsAdmin() || checkPermission(PERMISSIONS.ANALYTICS_READ);
  }, [checkIsAdmin, checkPermission]);

  // Check if user can manage projects
  const canManageProjects = useCallback(() => {
    return checkIsAdmin() || checkAnyPermission([
      PERMISSIONS.PROJECTS_WRITE,
      PERMISSIONS.PROJECTS_DELETE
    ]);
  }, [checkIsAdmin, checkAnyPermission]);

  // Check if user can configure security settings
  const canConfigureSecurity = useCallback(() => {
    return checkIsAdmin() || checkAnyPermission([
      PERMISSIONS.WAF_CONFIGURE,
      PERMISSIONS.BOT_CONFIGURE,
      PERMISSIONS.RATE_LIMIT_CONFIGURE,
      PERMISSIONS.FIREWALL_CONFIGURE
    ]);
  }, [checkIsAdmin, checkAnyPermission]);

  // Check if user can access API Security pages
  const canAccessApiSecurity = useCallback(() => {
    return checkIsAdmin() || checkAnyPermission([
      PERMISSIONS.API_SECURITY_READ,
      PERMISSIONS.API_SECURITY_CONFIGURE,
      PERMISSIONS.API_SECURITY_WRITE,
      PERMISSIONS.API_SECURITY_DELETE,
    ]);
  }, [checkIsAdmin, checkAnyPermission]);

  // Check if user is admin of a specific project
  const isProjectAdmin = useCallback((projectName) => {
    if (!projectName || !user?.projects) return false;
    
    // Find the project and check if user has admin role in it
    const project = user.projects.find(p => 
      p.projectName?.toLowerCase() === projectName?.toLowerCase()
    );
    
    if (!project) return false;
    
    // Check if the role in this project is an admin role
    const roleNameLower = project.roleName?.toLowerCase() || '';
    return roleNameLower === 'administrator' || 
           roleNameLower === 'admin' || 
           roleNameLower === 'project admin';
  }, [user]);

  // Get permissions for a specific site based on its project
  const getPermissionsForSite = useCallback((site) => {
    // If site has no project, use role permissions
    if (!site?.project_name && !site?.projectId && !site?.project_id) {
      const rolePermissions = user?.role?.permissions || [];
      const isGlobalAdmin = checkIsAdmin();
      
      return {
        permissions: rolePermissions,
        hasPermission: (perm) => rolePermissions.includes(perm) || isGlobalAdmin,
        canWrite: () => rolePermissions.includes(PERMISSIONS.SITES_WRITE) || isGlobalAdmin,
        canDelete: () => rolePermissions.includes(PERMISSIONS.SITES_DELETE) || isGlobalAdmin
      };
    }
    
    // Get the project identifier - could be project_name, projectId or project_id
    const siteProjectName = site?.project_name;
    const siteProjectId = site?.projectId || site?.project_id;
    
    // Find the project matching the site's project (case-insensitive for names)
    const project = user?.projects?.find(p => 
      (siteProjectName && p.projectName?.toLowerCase() === siteProjectName?.toLowerCase()) ||
      (siteProjectId && p.projectId === siteProjectId)
    );
    
    if (!project) {
      // If project not found, user has no access (unless they're a global admin)
      const isGlobalAdmin = checkIsAdmin();
      
      return {
        permissions: [],
        hasPermission: () => isGlobalAdmin,
        canWrite: () => isGlobalAdmin,
        canDelete: () => isGlobalAdmin
      };
    }
    
    // Check if user is admin of THIS specific project
    const isThisProjectAdmin = isProjectAdmin(siteProjectName || project.projectName);
    const projectPerms = project.permissions || [];
    
    // Return project-specific permissions
    return {
      permissions: projectPerms,
      hasPermission: (perm) => projectPerms.includes(perm) || isThisProjectAdmin,
      canWrite: () => projectPerms.includes(PERMISSIONS.SITES_WRITE) || isThisProjectAdmin,
      canDelete: () => projectPerms.includes(PERMISSIONS.SITES_DELETE) || isThisProjectAdmin
    };
  }, [user, checkIsAdmin, isProjectAdmin]);

  // Check if user has permission in their role (not project-specific)
  const hasRolePermission = useCallback((permission) => {
    // Admin users have all permissions
    if (checkIsAdmin()) {
      return true;
    }
    // Check only role permissions, not project permissions
    const rolePermissions = user?.role?.permissions || [];
    return rolePermissions.includes(permission);
  }, [user, checkIsAdmin]);

  // Load project permissions if projectId is provided
  const loadProjectPermissions = useCallback(async () => {
    if (projectId && rbacContext?.getProjectPermissions) {
      try {
        await rbacContext.getProjectPermissions(projectId);
      } catch (error) {
        console.error('Failed to load project permissions:', error);
      }
    }
  }, [projectId, rbacContext]);

  return {
    // Core permission data
    permissions: allPermissions,
    globalPermissions,
    projectPermissions,
    userRole: actualUserRole,
    user,
    
    // Permission checking functions
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    hasResourcePermission: checkResourcePermission,
    isAdmin: checkIsAdmin,
    isProjectAdmin,
    hasRolePermission,
    getPermissionsForSite,
    
    // Specific permission checks
    canAccessSites,
    canManageWAF,
    canManageUsers,
    canViewAnalytics,
    canManageProjects,
    canConfigureSecurity,
    canAccessApiSecurity,
    
    // Utility functions
    loadProjectPermissions,
    
    // Constants
    PERMISSIONS,
    RESOURCES,
    ACTIONS
  };
};

export default usePermissions;